// Rasid dizayn tizimi — React komponentlari. CSS klasslari docs/design-system/components/bundle.css'dan.
import { clsx } from "clsx";
import { forwardRef, useEffect, useId, useRef } from "react";
import { Icon } from "./icons";

/* ---------- Harakat ---------- */

/**
 * @param {{variant?: "default"|"primary"|"yashil"|"danger"|"ghost", size?: "md"|"sm", icon?: string,
 *   loading?: boolean, loadingText?: string, block?: boolean} & import("react").ButtonHTMLAttributes<HTMLButtonElement>} p
 */
export const Button = forwardRef(function Button(
  {
    variant = "default",
    size = "md",
    icon,
    loading,
    loadingText,
    block,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        "rs-btn",
        variant !== "default" && `rs-btn-${variant}`,
        size === "sm" && "rs-btn-sm",
        block && "rs-btn-block",
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="rs-spin" aria-hidden /> : icon ? <Icon name={icon} /> : null}
      {loading && loadingText ? loadingText : children}
    </button>
  );
});

export function IconButton({ icon, label, ...rest }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      icon={icon}
      aria-label={label}
      title={label}
      className="rs-btn-icon"
      {...rest}
    />
  );
}

/** @param {{options: {value:string,label:string}[], value:string, onChange:(v:string)=>void, label?:string}} p */
export function Segment({ options, value, onChange, label }) {
  return (
    <div className="rs-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      className="rs-sw"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

/* ---------- Holat ---------- */

const TONES = {
  kirim: "rs-chip-kirim",
  ogoh: "rs-chip-ogoh",
  xato: "rs-chip-xato",
  muhr: "rs-chip-muhr",
  yashil: "rs-chip-yashil",
  tun: "rs-chip-tun",
  xira: "rs-chip-xira",
};
/** @param {{tone?: keyof typeof TONES, children: any}} p */
export function Chip({ tone = "xira", children }) {
  return <span className={clsx("rs-chip", TONES[tone])}>{children}</span>;
}

/** @param {{tone?: "ogoh"|"xato"|"muhr"|"info", title?: string, children?: any, action?: any}} p */
export function Alert({ tone = "info", title, children, action }) {
  const icon = tone === "muhr" ? "stamp" : tone === "info" ? "info" : "warn";
  return (
    <div
      className={clsx("rs-alert", tone !== "info" && `rs-alert-${tone}`)}
      role={tone === "xato" ? "alert" : "status"}
    >
      <Icon name={icon} />
      <div>
        {title && <b>{title}</b>}
        {children && <span>{children}</span>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, children, action, coin = true }) {
  return (
    <div className="rs-empty">
      {coin && (
        <div className="rs-empty-art" aria-hidden>
          <svg viewBox="0 0 88 88" fill="none">
            <circle cx="44" cy="44" r="42" stroke="currentColor" strokeOpacity=".18" strokeDasharray="2 5" />
            <circle cx="44" cy="44" r="30" stroke="currentColor" strokeOpacity=".28" />
            <circle cx="44" cy="44" r="12" fill="var(--yashil)" />
            <circle cx="44" cy="44" r="12" stroke="var(--yashil-ustida)" strokeOpacity=".25" />
            <path
              d="M40 44h8M44 40v8"
              stroke="var(--yashil-ustida)"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && (
        <div className="rs-row" style={{ padding: 0, justifyContent: "center" }}>
          {action}
        </div>
      )}
    </div>
  );
}

export function Spinner({ label = "Yuklanmoqda" }) {
  return <span className="rs-spin rs-spin-lg" role="status" aria-label={label} />;
}

export function Skeleton({ h = 16, w = "100%" }) {
  return <span className="rs-skel" style={{ height: h, width: w }} aria-hidden />;
}

/* ---------- Forma ---------- */

/** @param {{label?: string, error?: string, hint?: string, full?: boolean, htmlFor?: string, children: any, required?: boolean}} p */
export function Field({ label, error, hint, full, htmlFor, children, required }) {
  return (
    <div className={clsx("rs-field", error && "is-err", full && "rs-full")}>
      {label && (
        <label htmlFor={htmlFor}>
          {label}
          {required && (
            <span className="rs-req" aria-hidden>
              {" "}
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span className="rs-err" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="rs-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export const Input = forwardRef(function Input({ className, mono, ...rest }, ref) {
  return <input ref={ref} className={clsx("rs-input", mono && "rs-num", className)} {...rest} />;
});

export const Textarea = forwardRef(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={clsx("rs-input rs-textarea", className)} {...rest} />;
});

/** @param {{options: {value:string,label:string}[], placeholder?: string} & import("react").SelectHTMLAttributes<HTMLSelectElement>} p */
export const Select = forwardRef(function Select({ options, placeholder, className, ...rest }, ref) {
  return (
    <span className="rs-select">
      <select ref={ref} className={clsx("rs-input", className)} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chev" />
    </span>
  );
});

export const Checkbox = forwardRef(function Checkbox({ label, hint, ...rest }, ref) {
  return (
    <label className="rs-check">
      <input ref={ref} type="checkbox" {...rest} />
      <span>
        {label}
        {hint && <small> — {hint}</small>}
      </span>
    </label>
  );
});

export function RadioGroup({ legend, name, options, value, onChange }) {
  return (
    <fieldset className="rs-fs">
      {legend && <legend>{legend}</legend>}
      {options.map((o) => (
        <label key={o.value} className="rs-radio">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

/* ---------- Ma'lumot ---------- */

export function Card({ title, extra, children, className, pad = true }) {
  return (
    <section className={clsx("rs-card", !pad && "rs-card-flush", className)}>
      {(title || extra) && (
        <header className="rs-card-h">
          {title && <h2>{title}</h2>}
          {extra && <div className="rs-card-x">{extra}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/** @param {{label:string, value:any, unit?:string, sub?:any, hero?:boolean, onClick?:()=>void}} p */
export function Kpi({ label, value, unit, sub, hero, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      className={clsx("rs-kpi", hero && "rs-kpi-hero", onClick && "rs-kpi-btn")}
      onClick={onClick}
      type={onClick ? "button" : undefined}
    >
      <small>{label}</small>
      <b className="rs-num">
        {value}
        {unit && <em>{unit}</em>}
      </b>
      {sub && <small>{sub}</small>}
    </Tag>
  );
}

const IC_TONE = { ogoh: "rs-ic-ogoh", xato: "rs-ic-xato", muhr: "rs-ic-muhr", kirim: "rs-ic-kirim" };
export function ListItem({ icon, tone, title, sub, end, onClick }) {
  const body = (
    <>
      <span className={clsx("rs-ic", tone && IC_TONE[tone])}>
        <Icon name={icon} />
      </span>
      <div>
        <b>{title}</b>
        {sub && <small>{sub}</small>}
      </div>
      {end}
    </>
  );
  return (
    <li>
      {onClick ? (
        <button type="button" className="rs-li-btn" onClick={onClick}>
          {body}
        </button>
      ) : (
        body
      )}
    </li>
  );
}

export function List({ children }) {
  return <ul className="rs-list">{children}</ul>;
}

/* ---------- Qatlam ---------- */

/**
 * Modal: Esc va fon bosilganda yopiladi, ochilganda birinchi maydonga fokus, yopilganda fokus qaytadi.
 * @param {{open:boolean, title:string, onClose:()=>void, children:any, footer?:any, size?:"md"|"sm"|"lg", dismissable?: boolean}} p
 */
export function Modal({ open, title, onClose, children, footer, size = "md", dismissable = true }) {
  const ref = useRef(/** @type {HTMLDivElement|null} */ (null));
  const titleId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const prev = /** @type {HTMLElement|null} */ (document.activeElement);
    const el = ref.current;
    const first = el?.querySelector(
      "[data-autofocus], input:not([type=hidden]), select, textarea, button.rs-btn-primary",
    );
    /** @type {HTMLElement|null} */ (first)?.focus();
    const onKey = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === "Escape" && dismissable) onClose();
      if (e.key === "Tab" && el) {
        const f = [
          ...el.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"),
        ].filter((n) => !(/** @type {HTMLButtonElement} */ (n).disabled));
        if (!f.length) return;
        const [a, z] = [f[0], f[f.length - 1]];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          /** @type {HTMLElement} */ (z).focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          /** @type {HTMLElement} */ (a).focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("rs-noscroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("rs-noscroll");
      prev?.focus?.();
    };
  }, [open, onClose, dismissable]);
  if (!open) return null;
  return (
    <div
      className="rs-scrim rs-scrim-fixed"
      onMouseDown={(e) => dismissable && e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        className={clsx("rs-modal", size === "sm" && "rs-modal-sm", size === "lg" && "rs-modal-lg")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <h2 id={titleId}>{title}</h2>
          {dismissable && <IconButton icon="x" label="Yopish" onClick={onClose} />}
        </header>
        <div className="rs-modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

/** Xavfli amalni tasdiqlash: sarlavha savol, matnda oqibat, xavfsiz tugma birinchi. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmText = "Tasdiqlash",
  danger,
  loading,
  onConfirm,
  onClose,
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>Qoldirish</Button>
          <Button variant={danger ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmText}
          </Button>
        </>
      }
    >
      <p className="rs-muted" style={{ margin: 0 }}>
        {children}
      </p>
    </Modal>
  );
}

/* ---------- Brend ---------- */

/** @param {{height?: number, onDark?: boolean}} p — onDark: doim to'q fon (yon panel); aks holda mavzuga qarab. */
export function Logo({ height = 26, onDark = false }) {
  if (onDark) return <img src="/brand/rasid-logo-oq.svg" height={height} alt="Rasid" className="rs-logo" />;
  return (
    <span className="rs-logo-auto">
      <img src="/brand/rasid-logo.svg" height={height} alt="Rasid" className="rs-logo-ink" />
      <img src="/brand/rasid-logo-oq.svg" height={height} alt="" aria-hidden className="rs-logo-paper" />
    </span>
  );
}
